import { type FormEvent, useEffect, useRef, useState } from "react";
import { ApiError, api, type CasoListItem, type Page } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  correoId: number;
  correoAsunto: string;
  correoFrom: string;
  onAsignado: () => void;
};

/** Cuántos casos se piden por búsqueda (tope del endpoint: 200). */
const TOPE = 50;

/**
 * Modal para asignar un correo (de la bandeja) a un caso existente.
 *
 * La búsqueda es **server-side**: le pega a `GET /api/casos?q=` con debounce,
 * o sea que encuentra por cliente, contacto, folio, TE, ticket JIMI, IMEI,
 * equipo y descripción — y sin importar acentos.
 *
 * Antes cargaba una tanda de casos y filtraba en memoria por TE/folio/cliente.
 * Pedía `?limit=200`, un parámetro que NO existe en el endpoint (el bueno es
 * `page_size`), así que FastAPI lo ignoraba y llegaban solo los 50 más
 * recientes: los casos viejos salían como "Sin coincidencias" aunque
 * existieran. Y este modal es justo el plan B para cuando el enganche
 * automático por hilo falla, que pasa sobre todo en casos viejos.
 */
export default function AsignarCasoModal({
  open,
  onOpenChange,
  correoId,
  correoAsunto,
  correoFrom,
  onAsignado,
}: Props) {
  const toast = useToast();
  const [casos, setCasos] = useState<CasoListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Al abrir: limpiar lo de la vez pasada y enfocar el buscador.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelectedId(null);
    const id = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [open]);

  // Búsqueda contra el servidor. Sin texto trae los más recientes (la lista
  // de arranque); con texto espera 300 ms para no pegarle en cada tecla.
  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    const q = query.trim();
    const id = setTimeout(
      () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        params.set("page_size", String(TOPE));
        api
          .get<Page<CasoListItem>>(`/api/casos?${params.toString()}`)
          .then((p) => {
            if (cancelado) return;
            setCasos(p.items);
            setTotal(p.total);
          })
          .catch((err) => {
            if (cancelado) return;
            const msg = err instanceof ApiError ? err.detail : "Error";
            toast.error("No pude cargar casos", msg);
          })
          .finally(() => {
            if (!cancelado) setLoading(false);
          });
      },
      q ? 300 : 0,
    );
    return () => {
      cancelado = true;
      clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (selectedId === null) return;
    setSubmitting(true);
    try {
      await api.post(`/api/correos/${correoId}/asignar`, { caso_id: selectedId });
      toast.success("Correo asignado al caso");
      onAsignado();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude asignar", msg);
    } finally {
      setSubmitting(false);
    }
  }

  const hayMas = total > casos.length;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Asignar correo a un caso"
      description={`De ${correoFrom} — ${correoAsunto.slice(0, 80)}`}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-ghost text-sm"
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="asignar-form"
            className="btn-primary text-sm"
            disabled={selectedId === null || submitting}
          >
            {submitting ? "Asignando..." : "Asignar"}
          </button>
        </>
      }
    >
      {/* La lista CRECE con el modal. Estaba clavada en 288 px dentro de una
          ventana que llena el 88% de la pantalla: se veían 3 casos con media
          pantalla vacía abajo (Joe: «¿por qué siempre diseñas en chico?»).
          Agrandar el contenedor sin dejar que el contenido lo aproveche es
          peor que no agrandarlo. */}
      <form id="asignar-form" onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Qué busca, a la vista (antes era un tooltip): coincide con
            `listar_casos` en api/casos/listado.py. */}
        <div className="shrink-0">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // La selección se hace sobre lo que se ve: si cambia la búsqueda,
              // el caso elegido puede desaparecer de la lista.
              setSelectedId(null);
            }}
            placeholder="Buscar por cliente, TE, folio, IMEI…"
            className="input w-full"
            autoComplete="off"
            aria-describedby="asignar-busqueda-ayuda"
          />
          <p id="asignar-busqueda-ayuda" className="mt-1 text-[11px] text-zinc-500">
            Busca en todos los casos, abiertos y cerrados: cliente, contacto (nombre o correo),
            folio, TE, ticket de Jimi, IMEI, marca, modelo y descripción. No distingue mayúsculas
            ni acentos.
          </p>
        </div>

        <div className="scroll-sutil min-h-[12rem] flex-1 overflow-y-auto rounded border border-ink-700">
          {loading ? (
            <p className="p-3 text-sm text-zinc-500">Buscando casos...</p>
          ) : casos.length === 0 ? (
            <p className="p-3 text-sm text-zinc-500">
              {query.trim() ? "Sin coincidencias" : "Sin casos"}
            </p>
          ) : (
            <ul className="divide-y divide-ink-700">
              {casos.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full text-left p-2 hover:bg-ink-800 flex items-start gap-3 ${
                      selectedId === c.id ? "bg-accent-500/10 ring-1 ring-accent-500/40" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-accent-400">{c.folio}</span>
                        {c.ticket_externo && (
                          <span className="font-mono text-xs text-zinc-400">
                            {c.ticket_externo}
                          </span>
                        )}
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-ink-700 text-zinc-400">
                          {c.estado}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-200 truncate">{c.cliente_nombre}</p>
                      {(c.marca || c.modelo) && (
                        <p className="text-xs text-zinc-500 truncate">
                          {[c.marca, c.modelo].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {/* De qué se trata. Sin esto, dos casos del mismo cliente
                          con el mismo equipo se ven idénticos y se elige a
                          ciegas — Joe: «va a haber pedos con eso». */}
                      {c.descripcion && (
                        <p className="mt-0.5 text-xs leading-snug text-zinc-400 line-clamp-2">
                          {c.descripcion}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Decir en voz alta que la lista está recortada: si no, "no aparece"
            se confunde con "no existe" — que es el bug que se acaba de cerrar. */}
        {!loading && hayMas && (
          <p className="shrink-0 text-xs text-zinc-500">
            Mostrando {casos.length} de {total} casos
            {query.trim() ? " que coinciden" : ""} — afina la búsqueda para ver
            el que falta.
          </p>
        )}
      </form>
    </Modal>
  );
}
